import createClient from 'openapi-fetch'
import type { paths } from './schema'

/**
 * API client.
 *
 * The access token lives in memory only. The refresh token is an HttpOnly cookie that
 * the browser never exposes to JavaScript; every state-changing call carries the CSRF
 * token from the readable companion cookie. A 401 triggers exactly one silent refresh
 * before the call is retried.
 */

let accessToken: string | null = null
let refreshInFlight: Promise<boolean> | null = null
const listeners = new Set<(token: string | null) => void>()

export function setAccessToken(token: string | null): void {
  accessToken = token
  listeners.forEach((listener) => listener(token))
}

export function getAccessToken(): string | null {
  return accessToken
}

export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)backoffice_csrf=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

async function refreshOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json', 'x-csrf-token': csrfToken() ?? '' },
          body: '{}',
        })
        if (!response.ok) {
          setAccessToken(null)
          return false
        }
        const body = (await response.json()) as { access_token: string }
        setAccessToken(body.access_token)
        return true
      } catch {
        setAccessToken(null)
        return false
      } finally {
        refreshInFlight = null
      }
    })()
  }
  return refreshInFlight
}

function decorate(init: RequestInit | undefined): RequestInit {
  const headers = new Headers(init?.headers)
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)
  const csrf = csrfToken()
  if (csrf) headers.set('x-csrf-token', csrf)
  return { ...init, headers, credentials: 'include' }
}

/**
 * Headers are copied from the built Request, never rebuilt from ``init``: the generated
 * client sets Content-Type on the Request itself, and replacing the header set from
 * ``init`` would silently drop it.
 */
function authorize(source: Request): Request {
  const headers = new Headers(source.headers)
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)
  const csrf = csrfToken()
  if (csrf) headers.set('x-csrf-token', csrf)
  return new Request(source, { headers, credentials: 'include' })
}

const authFetch: typeof fetch = async (input, init) => {
  const request = input instanceof Request ? input : new Request(input as RequestInfo, init)
  const retryable = request.clone()

  let response = await fetch(authorize(request))
  const isAuthRoute = request.url.includes('/api/auth/') || request.url.includes('/api/setup')
  if (response.status === 401 && !isAuthRoute && (await refreshOnce())) {
    response = await fetch(authorize(retryable))
  }
  return response
}

export const api = createClient<paths>({ baseUrl: '', fetch: authFetch })

export type ApiError = { code: string; message: string; details?: Record<string, unknown> | null }

/** Pull the typed error body out of a failed response, with a German fallback. */
export function errorMessage(error: unknown, fallback = 'Etwas ist schiefgelaufen.'): string {
  if (error && typeof error === 'object' && 'error' in error) {
    const body = (error as { error?: ApiError }).error
    if (body?.message) return body.message
  }
  if (error instanceof Error) return error.message
  return fallback
}

/**
 * Types a preview frame may render. A blob: URL inherits this origin, so the type on
 * the Blob decides whether bytes from a mail attachment become a document or a script.
 * Pinning it here means an HTML payload can never be parsed as HTML, whatever header
 * the response carried.
 */
const RENDERABLE = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'text/plain',
])

export type PreviewBlob = { url: string; mediaType: string; renderable: boolean }

/** Authenticated blob URL for originals - the viewer cannot send a bearer header itself. */
export async function fetchBlobUrl(sha: string, expected = 'application/pdf'): Promise<PreviewBlob> {
  let response = await fetch(`/api/blobs/${sha}`, decorate({ method: 'GET' }))
  if (!response.ok && response.status === 401 && (await refreshOnce())) {
    response = await fetch(`/api/blobs/${sha}`, decorate({ method: 'GET' }))
  }
  if (!response.ok) throw new Error('Datei konnte nicht geladen werden.')

  const served = (response.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase()
  const renderable = RENDERABLE.has(served)
  // Re-type deliberately: never carry over whatever the bytes claimed to be.
  const mediaType = renderable ? served : expected
  const raw = await response.blob()
  const typed = new Blob([raw], { type: renderable ? mediaType : 'application/octet-stream' })
  return { url: URL.createObjectURL(typed), mediaType, renderable }
}

export async function uploadLetter(file: File, hintText?: string): Promise<Response> {
  const form = new FormData()
  form.append('file', file)
  if (hintText) form.append('hint_text', hintText)
  return authFetch('/api/letters', { method: 'POST', body: form })
}
